import { useCallback, useEffect } from "react";
import { getNativeCurrency } from "./web3Config";
import { BLOCKCHAINS, isEvmChain } from "./providers";
import { getAccountInfo } from "./algorand/algod.service";
import { getBalance } from "./web3/balance";
import { useAppDispatch, useAppSelector } from "../services/hooks.service";
import { useActiveAccount } from "./useActiveAccount";
import { setCurrency } from "../redux/_actions/user.action";
import { amountFormate } from "../_utils";
import { SolanaNetwork } from "./solana/network";
import { SolanaConnector } from "./solana/solana.service";
import { PublicKey } from "@solana/web3.js";

const solanaConnector = SolanaConnector.getInstance();

interface FetchBalanceResult {
  decimals: number;
  formatted: string,
  symbol: string,
  value: BigInt,
}

export const useBalances = () => {
  const dispatch = useAppDispatch();
  const activeWallet = useActiveAccount();
  const chain = useAppSelector(state => state.network.chain);
  const nativeCurrency = useAppSelector(state => state.user.walletCurrency);

  const getBalances = useCallback(async (): Promise<FetchBalanceResult> => {
    // console.log('getBalances', { ...activeWallet, networkChain: chain })
    if (!activeWallet?.address || activeWallet.chain !== chain) {
      return null;
    }

    if(isEvmChain(chain)) {
      const balance = await getBalance(activeWallet);
      let evmNativeCurrency = nativeCurrency
      if (!evmNativeCurrency) {
        evmNativeCurrency = getNativeCurrency(chain)
      }
      return {
        decimals: evmNativeCurrency.decimals,
        formatted: amountFormate(String(balance), evmNativeCurrency.decimals, 6),
        symbol: evmNativeCurrency.symbol,
        value: balance
      }
    } else if(chain === BLOCKCHAINS.ALGORAND) {
      const accountInfo = await getAccountInfo(activeWallet?.address);
      return {
        decimals: 6,
        formatted: amountFormate(String(accountInfo.amount), 6, 6),
        value: BigInt(accountInfo.amount),
        symbol: "ALGO"
      };
    } else if(chain === BLOCKCHAINS.SOLANA) {
      const amount = await SolanaNetwork.connection.getBalance(new PublicKey(solanaConnector.address));
      return {
        decimals: 9,
        formatted: amountFormate(String(amount), 9, 6),
        symbol: 'SOL',
        value: BigInt(amount),
      }
    }

    return null;
  }, [activeWallet?.address, chain, nativeCurrency?.name]);

  useEffect(() => {
    chain && dispatch(setCurrency(getNativeCurrency(chain)));
  }, [chain]);

  return {
    nativeCurrency,
    getBalances,
  }
}
